import {
  Modal,
  ModalContent,
  type ModalHook,
  ModalSize,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import React, { type ComponentPropsWithoutRef, type FC, useState } from 'react';

import { isServer } from '../utils/constants';
import { BASE_SEARCH_PATH } from '../utils/search';

import { LoginFormV2 } from './forms/LoginFormV2';
import { SignupForm } from './forms/SignupForm';

type LoginModalMode = 'login' | 'signup';

interface LoginModalHookProps
  extends Pick<
    ComponentPropsWithoutRef<typeof ModalContent>,
    'preventCloseOnOverlayClick' | 'hideCloseButton'
  > {
  defaultMode?: LoginModalMode;
  skipSignupForm?: boolean;
}

interface LoginModalProps extends LoginModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const LoginModalContent: FC<LoginModalProps> = ({
  open,
  setOpen,
  defaultMode = 'signup',
  skipSignupForm = true,
  ...modalContentProps
}) => {
  const [mode, setMode] = useState<LoginModalMode>(defaultMode);

  const redirectUrl = isServer ? BASE_SEARCH_PATH : window.location.href;

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent size={ModalSize.SMALL} {...modalContentProps}>
        <div className="p-6">
          {mode === 'login' ? (
            <LoginFormV2
              className="w-full text-center"
              returnTo={redirectUrl}
              isModal
              setMode={setMode}
            />
          ) : (
            <SignupForm
              className="w-full text-center"
              isModal
              setMode={setMode}
              skipForm={skipSignupForm}
              returnTo={redirectUrl}
            />
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export const useLoginModal: ModalHook<LoginModalHookProps> = () => {
  const [open, setOpen] = useControlledModalState();
  const LoginModal: FC<LoginModalHookProps> = (props) => {
    return open ? (
      <LoginModalContent {...props} open={open} setOpen={setOpen} />
    ) : null;
  };
  return { open, setOpen, Modal: LoginModal };
};
